import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { PosProductCard } from './pos-product-card'
import type { Product } from '@/lib/api/product-api'
import {
    useInventoryByWarehouse,
    useWarehouses,
} from '@/hooks/use-inventory-query'
import { useProducts } from '@/hooks/use-product-query'
import { m } from '@/lib/paraglide/messages'

const ITEMS_PER_PAGE = 21

interface Props {
    searchTerm: string
    onProductClick: (product: Product) => void
}

export function PosProductGrid({ searchTerm, onProductClick }: Props) {
    const {
        data: productsResp,
        isLoading,
        isError,
    } = useProducts({ per_page: 200 })
    const products = productsResp?.data ?? []
    // Tồn theo warehouse mặc định, build map product_id → tổng tồn để hiện
    // badge "Tồn: X" trên từng PosProductCard.
    const { data: warehouses = [] } = useWarehouses()
    const defaultWarehouseId =
        warehouses.find((w) => w.is_default)?.id ?? warehouses[0]?.id
    const { data: inventoryData = [] } =
        useInventoryByWarehouse(defaultWarehouseId)
    const stockMap = useMemo(
        () =>
            inventoryData.reduce<Record<string, number>>((acc, row) => {
                acc[row.product_id] = (acc[row.product_id] ?? 0) + row.quantity
                return acc
            }, {}),
        [inventoryData],
    )
    const [page, setPage] = useState(0)

    const filtered = useMemo(() => {
        if (!searchTerm) return products.filter((p) => p.is_active)
        const q = searchTerm.toLowerCase()
        return products.filter(
            (p) =>
                p.is_active &&
                (p.name.toLowerCase().includes(q) ||
                    p.sku.toLowerCase().includes(q)),
        )
    }, [products, searchTerm])

    // Reset page when search changes
    useEffect(() => setPage(0), [searchTerm])

    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE))
    const pageItems = filtered.slice(
        page * ITEMS_PER_PAGE,
        (page + 1) * ITEMS_PER_PAGE,
    )

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                {m.pos_product_loading()}
            </div>
        )
    }

    if (isError) {
        return (
            <div className="flex-1 flex items-center justify-center text-sm text-destructive">
                {m.pos_product_load_error()}
            </div>
        )
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-auto p-2">
                <div className="grid grid-cols-3 gap-2">
                    {pageItems.map((product) => (
                        <PosProductCard
                            key={product.id}
                            product={product}
                            stock={stockMap[product.id]}
                            onClick={onProductClick}
                        />
                    ))}
                </div>
                {filtered.length === 0 && (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        {m.pos_product_not_found()}
                    </div>
                )}
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-center gap-3 px-2 py-1.5 border-t text-sm shrink-0">
                <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                >
                    <ChevronLeft className="size-4" />
                </button>
                <span className="text-muted-foreground">
                    {page + 1}/{totalPages}
                </span>
                <button
                    type="button"
                    onClick={() =>
                        setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                >
                    <ChevronRight className="size-4" />
                </button>
            </div>
        </div>
    )
}
